import { ApiApp, ApiAppConfiguration, ApiAppDeployment, ApiAppOrigin, ApiAppStage, ApiAppGitRef, ApiListAppsResponse, ApiAppDeploymentStatus, ApiAsyncDeployRequest, ApiAppStageRequest, ApiAppStageResponse, ApiAppDeployRequest, ApiAppDeployResponse, ApiAppDeleteRequest, ApiAppUpdateRequest, ApiAppCreateRequest, ApiAppGitImportParams, ApiAppGitImportResponse, ApiAppDeploymentsRequest, ApiAppManifest, ApiAppStoreArchiveResponse } from "./api-types";
import { App, AppConfiguration, AppDeployment, AppOrigin, AppStage, AppGitRef, ListAppsResponse, AppDeploymentStatus, AsyncDeployRequest, AppStageRequest, AppStageResponse, AppDeployRequest, AppDeployResponse, AppDeleteRequest, AppUpdateRequest, AppCreateRequest, AppGitImportParams, AppGitImportResponse, AppDeploymentsRequest, AppFormSource, AppFormBindings, AppFormDetails, AppFormBuildOptions, AppForm, AppManifest, AppStoreArchiveResponse, AppMeta, AppPodInfo } from "./ui-types";
import { statusToStateDisplay } from "../../models/resource/mappers";
import { AppUtils } from "../../utils/application";
import { APPLICATION_SOURCE_TYPE, APPLICATION_BUILD_MODE, APPLICATION_MANIFEST_SOURCE_TYPE } from "../../types";
import { parse } from "../../utils/url";
import { BuildCache } from "./actions/restage";
import { identity, pickBy } from "lodash";

function toAppStage(apiStage: ApiAppStage): AppStage {
    return {
        builder: apiStage.builder,
        buildMode: apiStage.buildMode,
        dockerfilePath: apiStage.dockerfilePath,
    };
}

function toAppConfiguration(apiConfiguration: ApiAppConfiguration, fromManifest?: boolean): AppConfiguration {
    return {
        appChart: apiConfiguration.appchart,
        configurations: apiConfiguration.configurations,
        boundConfigurations: apiConfiguration.bound_configurations,
        environment: apiConfiguration.environment,
        instances: apiConfiguration.instances,
        routes: fromManifest ? [] : apiConfiguration.routes,
        services: apiConfiguration.services,
        settings: apiConfiguration.settings,
    };
}

function toAppDeployment(apiDeployment: ApiAppDeployment | undefined): AppDeployment | undefined {
    if (!apiDeployment) return undefined;
    return {
        active: apiDeployment.active,
        createdAt: apiDeployment.createdAt,
        desiredReplicas: apiDeployment.desiredreplicas,
        name: apiDeployment.name,
        readyReplicas: apiDeployment.readyreplicas,
        replicas: apiDeployment.replicas,
        routes: apiDeployment.routes,
        stageId: apiDeployment.stage_id,
        status: apiDeployment.status,
        username: apiDeployment.username,
    };
}

function toAppOrigin(apiOrigin: ApiAppOrigin): AppOrigin {
    let Kind = apiOrigin.Kind;
    // Determine the kind of the app origin if it's not explicitly set (for example from manifest)
    if (!Kind) {
        if (apiOrigin.archive || apiOrigin.path) Kind = APPLICATION_MANIFEST_SOURCE_TYPE.PATH;
        if (apiOrigin.container) Kind = APPLICATION_MANIFEST_SOURCE_TYPE.CONTAINER;
        if (apiOrigin.git) Kind = APPLICATION_MANIFEST_SOURCE_TYPE.GIT;
        else Kind = APPLICATION_MANIFEST_SOURCE_TYPE.NONE;
    }
    return {
        Kind: Kind,
        archive: apiOrigin.archive,
        container: apiOrigin.container,
        git: apiOrigin.git ? toAppGitRef(apiOrigin.git) : undefined,
        path: apiOrigin.path,
    };
}

function toApiAppGitRef(gitRef: AppGitRef): ApiAppGitRef {
    return {
        branch: gitRef.branch,
        provider: gitRef.provider,
        repository: gitRef.repository,
        revision: gitRef.revision,
        gitconfig: gitRef.gitconfig,
    };
}

function toApiAppOrigin(origin: AppOrigin): ApiAppOrigin {
    return {
        Kind: origin.Kind,
        archive: origin.archive,
        container: origin.container,
        git: origin.git ? toApiAppGitRef(origin.git) : undefined,
        path: origin.path,
    };
}

function toAppGitRef(apiGitRef: ApiAppGitRef): AppGitRef {
    return {
        branch: apiGitRef.branch,
        provider: apiGitRef.provider,
        repository: apiGitRef.repository ?? apiGitRef.url,
        revision: apiGitRef.revision,
        gitconfig: apiGitRef.gitconfig,
    };
}

export function toApp(apiApp: ApiApp): App {
    const origin = toAppOrigin(apiApp.origin);
    const sourceType = AppUtils.getSourceType(origin);
    const hasGit = !!(apiApp.origin?.git?.repository || apiApp.origin?.git?.url);
    const hasBlob = !!apiApp.blobuid;
    
    return {
        meta: apiApp.meta,
        configuration: toAppConfiguration(apiApp.configuration),
        deployment: toAppDeployment(apiApp.deployment),
        imageUrl: apiApp.image_url,
        origin: origin,
        stageId: apiApp.stage_id,
        staging: toAppStage(apiApp.staging),
        stagingStatus: apiApp.stagingstatus,
        status: apiApp.status,
        statusMessage: apiApp.statusmessage,
        stateDisplay: statusToStateDisplay[apiApp.status],
        blobUid: apiApp.blobuid,
        canRetryBuild: sourceType === APPLICATION_SOURCE_TYPE.CONTAINER_URL ? false : hasGit || hasBlob,
    };
}

export function toListAppsResponse(apiResponse: ApiListAppsResponse): ListAppsResponse {
    const { items, ...paginationMetadata } = apiResponse;
    return {
        items: apiResponse.items.map(toApp),
        ...paginationMetadata,
    };
}

export function toAppDeploymentStatus(apiDeploymentStatus: ApiAppDeploymentStatus): AppDeploymentStatus {
    return {
        app: apiDeploymentStatus.app,
        error: apiDeploymentStatus.error,
        finishedAt: apiDeploymentStatus.finishedAt,
        id: apiDeploymentStatus.id,
        image: apiDeploymentStatus.image,
        routes: apiDeploymentStatus.routes,
        stageId: apiDeploymentStatus.stage_id,
        startedAt: apiDeploymentStatus.startedAt,
        status: apiDeploymentStatus.status,
        warnings: apiDeploymentStatus.warnings,
    };
}

export function toApiAsyncDeployRequest(app: AsyncDeployRequest ): ApiAsyncDeployRequest {
    return {
        app: app.app,
        blobuid: app.blobUid,
        builderimage: app.builderImage,
        buildmode: app.buildMode,
        dockerfilepath: app.dockerfilePath,
        image: app.image,
        origin: toApiAppOrigin(app.origin),
    };
}

export function toApiAppStageRequest(app: AppStageRequest): ApiAppStageRequest {
    return {
        app: app.app,
        blobuid: app.blobUid,
        builderimage: app.builderImage,
        buildmode: app.buildMode,
        dockerfilepath: app.dockerfilePath,
        image: app.image,
    };
}

export function toAppStageResponse(apiResponse: ApiAppStageResponse): AppStageResponse {
    return {
        stage: apiResponse.stage,
        image: apiResponse.image,
    };
}

export function toApiAppDeployRequest(app: AppDeployRequest): ApiAppDeployRequest {
    return {
        app: app.app,
        image: app.image,
        origin: toApiAppOrigin(app.origin),
        stage: {
            id: app.stage.id,
        }
    };
}

export function toAppDeployResponse(apiResponse: ApiAppDeployResponse): AppDeployResponse {
    return {
        routes: apiResponse.routes,
        warnings: apiResponse.warnings,
    };
}

export function toApiAppDeleteRequest(app: AppDeleteRequest): ApiAppDeleteRequest {
    return {
        deleteImage: !!app.deleteImage,
        deletePVC: !!app.deletePVC,
        unmounted: !!app.unmounted,
    };
};

export function toApiAppUpdateRequest(app: AppUpdateRequest): ApiAppUpdateRequest {
    return {
        appchart: app.appChart,
        configurations: app.configurations,
        environment: app.environment,
        instances: app.instances,
        replace_env: app.replaceEnv,
        restart: app.restart,
        routes: app.routes,
        settings: app.settings,
    };
}

export function toApiAppCreateRequest(app: AppCreateRequest): ApiAppCreateRequest {
    return {
        configuration: toApiAppUpdateRequest(app.configuration),
        name: app.name,
    };
}

export function toApiAppDeploymentsRequest(app: AppDeploymentsRequest): ApiAppDeploymentsRequest {
    return {
        app: app.app,
        blobuid: app.blobUid,
        builderimage: app.builderImage,
        buildmode: app.buildMode,
        dockerfilepath: app.dockerfilePath,
        image: app.image,
        origin: toApiAppOrigin(app.origin),
    };
}

export function toApiAppGitImportParams(params: AppGitImportParams): ApiAppGitImportParams {
    return {
        giturl: params.gitUrl,
        gitrev: params.gitRev,
    };
}

export function toAppGitImportResponse(response: ApiAppGitImportResponse): AppGitImportResponse {
    return {
        blobUid: response.blobuid,
        branch: response.branch,
        revision: response.revision,
    };
}

export function toAppManifest(apiManifest: ApiAppManifest): AppManifest {
    return {
        meta: {
            name: '',
            namespace: apiManifest.namespace,
            createdAt: '',
        },
        configuration: toAppConfiguration(apiManifest.configuration, true),
        origin: toAppOrigin(apiManifest.origin),
        staging: toAppStage(apiManifest.staging),
    };
}

export function toAppStoreArchiveResponse(apiResponse: ApiAppStoreArchiveResponse): AppStoreArchiveResponse {
    return {
        blobUid: apiResponse.blobuid,
    };
}

export function toInstanceStats(instances: AppPodInfo[], prop: 'millicpus' | 'memoryBytes') {
    const stats = instances.reduce((res, r) => {
      if (r[prop] >= res.max) {
        res.max = r[prop];
      }
      if (r[prop] <= res.min) {
        res.min = r[prop];
      }
      res.total += r[prop];

      return res;
    }, {
      min: instances[0]?.[prop] || 0, max: 0, total: 0
    });

    const avg = instances.length ? (stats.total / instances.length).toFixed(2) : 0;

    return {
      ...stats,
      avg: avg === '0.00' ? 0 : avg,
    };
}

export function toAppSourceDetails(application: AppForm, t: any) {
    console.log(application);
  const appChart = {
    label: 'App Chart',
    value: application.buildOptions.appChart
  };

  const builder = {
    label: '',
    value: ''
  };
  if (application.buildOptions.buildMode === 'dockerfile') {
    builder.label = 'Dockerfile Path';
    builder.value = application.buildOptions.dockerfilePath ?? '';
  } else {
    builder.label = 'Builder Image';
    builder.value = application.buildOptions.builderImage ?? '';
  }

  const sourceType = application.source.type;

  switch (sourceType) {
    case APPLICATION_SOURCE_TYPE.FOLDER:
    case APPLICATION_SOURCE_TYPE.ARCHIVE:
      return {
        label:   'File system',
        icon:    'icon-file',
        details: [
          {
            label: 'Original Name',
            value: application.source[sourceType]?.name
          }, appChart, builder
        ]
      };
    case APPLICATION_SOURCE_TYPE.GIT_URL:
      return {
        label:   'Git',
        icon:    'icon-file',
        details: [
          {
            label: 'Url',
            value: application.source.gitUrl?.url
          }, {
            label: 'Revision',
            icon:  'icon-commit',
            value: application.source.gitUrl?.branch
          }, appChart, builder
        ]
      };
    case APPLICATION_SOURCE_TYPE.GIT_HUB:
    case APPLICATION_SOURCE_TYPE.GIT_LAB:
      return {
        label:   t(`epinio.applications.gitSource.${ sourceType }.label`),
        icon:    `icon-${ sourceType }`,
        details: [
          {
            label: 'Url',
            value: application.source.github?.url
          }, {
            label: 'Revision',
            icon:  'icon-commit',
            value: application.source.github?.commit
          }, {
            label: 'Branch',
            icon:  'icon-commit',
            value: application.source.github?.branch
          }, appChart, builder
        ]
      };
    case APPLICATION_SOURCE_TYPE.CONTAINER_URL:
      return {
        label:   'Container',
        icon:    'icon-docker',
        details: [{
          label: 'Image',
          value: application.source.containerUrl?.url
        }, appChart
        ]
      };
    default:
      return undefined;
    }
}

// FORM MAPPERs
function toAppFormGitData(gitData: AppGitRef): AppFormSource['github' | 'gitlab'] {
    const url = gitData.repository;
    const parsed = parse(url || '');

    const parts = parsed.path.split('/');
    return {
        userOrOrg: parts[1],
        branch: gitData.branch || '',
        commit: gitData.revision || '',
        repository: parts[2],
        gitConfig: gitData.gitconfig,
        url: url || '',
    };
}

export function toAppFormSource(origin: AppOrigin): AppFormSource {
    const sourceType = AppUtils.getSourceType(origin);
    switch (sourceType) {
        case APPLICATION_SOURCE_TYPE.ARCHIVE:
            return {
                type: APPLICATION_SOURCE_TYPE.ARCHIVE,
                archive: {
                    name: origin.path || '',
                },
            };
        case APPLICATION_SOURCE_TYPE.FOLDER:
            return {
                type: APPLICATION_SOURCE_TYPE.FOLDER,
                folder: {
                    name: origin.path || '',
                },
            };
        case APPLICATION_SOURCE_TYPE.CONTAINER_URL:
            return {
                type: APPLICATION_SOURCE_TYPE.CONTAINER_URL,
                containerUrl: {
                    url: origin.container || '',
                },
            };
        case APPLICATION_SOURCE_TYPE.GIT_URL:
            return {
                type: APPLICATION_SOURCE_TYPE.GIT_URL,
                gitUrl: {
                    gitConfig: origin.git?.gitconfig || '',
                    url: origin.git?.repository || '',
                    branch: origin.git?.revision || '',
                }
            };
        case APPLICATION_SOURCE_TYPE.GIT_HUB:
            return {
                type: APPLICATION_SOURCE_TYPE.GIT_HUB,
                github: origin.git ? toAppFormGitData(origin.git) : { userOrOrg: '', branch: '', commit: '', repository: '', gitConfig: '', url: '' },
            };
        case APPLICATION_SOURCE_TYPE.GIT_LAB:
            return {
                type: APPLICATION_SOURCE_TYPE.GIT_LAB,
                gitlab: origin.git ? toAppFormGitData(origin.git) : { userOrOrg: '', branch: '', commit: '', repository: '', gitConfig: '', url: '' },
            };
        default:
            throw new Error(`Unsupported source type: ${sourceType}`);
    }
}

export function toAppFormBuildOptions(app: App | AppManifest): AppFormBuildOptions {
    return {
        appChart: app.configuration.appChart || '',
        buildMode: app.staging.buildMode || APPLICATION_BUILD_MODE.BUILDPACK,
        builderImage: app.staging.builder || '',
        dockerfilePath: app.staging.dockerfilePath || '',
        builderImagesForbidden: false,
    };
}

export function toAppFormDetails(app: App | AppManifest): AppFormDetails {
    return {
        name: app.meta.name,
        namespace: app.meta.namespace,
        instances: app.configuration.instances,
        routes: [...(app.configuration.routes || [])],
        settings: {...(app.configuration.settings || {})},
        environment: [...Object.entries(app.configuration.environment || {}).map(([key, value]) => ({ key, value }))],
    }
}

export function toAppFormBindings(app: App | AppManifest): AppFormBindings {
    return {
        services: [...(app.configuration.services || [])],
        configurations: (app.configuration.boundConfigurations || []).filter(c => c.type === 'custom').map(c => c.name),
        serviceConfigurations: (app.configuration.boundConfigurations || []).filter(c => c.type === 'service').map(c => c.name),
    };
}

export function toAppForm(app: App | AppManifest): AppForm {
    return {
        source: toAppFormSource(app.origin),
        buildOptions: toAppFormBuildOptions(app),
        details: toAppFormDetails(app),
        bindings: toAppFormBindings(app),
    };
}

export function appFormToCreateRequest(form: AppForm): AppCreateRequest {
    return {
        configuration: appFormToUpdateRequest(form, true, false),
        name: form.details.name,
    };
}

export function appFormToUpdateRequest(form: AppForm, isCreate?: boolean, canRestart?: boolean): AppUpdateRequest {
    return {
        appChart: form.buildOptions.appChart ?? '',
        configurations: [...form.bindings.configurations, ...form.bindings.serviceConfigurations],
        environment: form.details.environment.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {}),
        instances: form.details.instances,
        replaceEnv: !isCreate,
        restart: !!canRestart,
        routes: form.details.routes.length > 0 ? form.details.routes : null,
        settings: pickBy(form.details.settings, identity) || null,
    };
}

function appFormToAppMeta(form: AppForm): AppMeta {
    return {
        name: form.details.name,
        namespace: form.details.namespace,
        createdAt: ''
    };
}

function appFormToAppOrigin(form: AppForm): AppOrigin {
    switch (form.source.type) {
        case APPLICATION_SOURCE_TYPE.ARCHIVE:
            return {
                Kind: APPLICATION_MANIFEST_SOURCE_TYPE.PATH,
                archive: true,
                path: form.source.archive?.name,
            };
        case APPLICATION_SOURCE_TYPE.FOLDER:
            return {
                Kind: APPLICATION_MANIFEST_SOURCE_TYPE.PATH,
                path: form.source.folder?.name,
            };
        case APPLICATION_SOURCE_TYPE.CONTAINER_URL:
            return {
                Kind: APPLICATION_MANIFEST_SOURCE_TYPE.CONTAINER,
                container: form.source.containerUrl?.url,
            };
        case APPLICATION_SOURCE_TYPE.GIT_URL:
            return {
                Kind: APPLICATION_MANIFEST_SOURCE_TYPE.GIT,
                git: {
                    revision: form.source.gitUrl?.branch,
                    repository: form.source.gitUrl?.url,
                    gitconfig: form.source.gitUrl?.gitConfig,
                },
            };
        case APPLICATION_SOURCE_TYPE.GIT_HUB:
        case APPLICATION_SOURCE_TYPE.GIT_LAB:
            return {
                Kind: APPLICATION_MANIFEST_SOURCE_TYPE.GIT,
                git: {
                    revision: form.source[form.source.type]?.commit,
                    repository: form.source[form.source.type]?.url,
                    branch: form.source[form.source.type]?.branch,
                    provider: form.source.type,
                    gitconfig: form.source[form.source.type]?.gitConfig,
                },
            };
        default:
            throw new Error(`Unsupported application source type: ${form.source.type}`);
    }
}

export function appFormToAsyncDeployRequest(form: AppForm, cache: BuildCache): AsyncDeployRequest {
    const isContainer = form.source.type === 'containerUrl';
    return {
        app: cache.appMeta || appFormToAppMeta(form),
        blobUid: isContainer ? undefined : cache.blobUid,
        builderImage: isContainer ? undefined : form.buildOptions.builderImage,
        origin: appFormToAppOrigin(form),
        buildMode: isContainer ? undefined : form.buildOptions.buildMode,
        dockerfilePath: isContainer ? undefined : form.buildOptions.dockerfilePath,
        image: isContainer ? form.source.containerUrl?.url : undefined,
    };
}

export function resolveGitParams(source: AppFormSource): { url?: string; rev?: string; gitConfig?: string } {
  switch(source.type) {
    case APPLICATION_SOURCE_TYPE.GIT_URL:
      return {
        url: source.gitUrl?.url,
        rev: source.gitUrl?.branch,
        gitConfig: source.gitUrl?.gitConfig,
      };
    case APPLICATION_SOURCE_TYPE.GIT_HUB:
    case APPLICATION_SOURCE_TYPE.GIT_LAB: {
      const gitSource = source[source.type];
      return {
        url: gitSource?.url,
        rev: gitSource?.commit,
        gitConfig: gitSource?.gitConfig,
      };
    }
    default:
      return {};
  }
};
    

